import { SignupIntent } from '@ps/models/lib/signup';
import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import { kAnalyticsSessionParamsKey } from './analytics/sessionParams';

/**
 * THE TYPE SCALE's steps (`tailwind.config.ts` `fontSize` — see
 * [TYPOGRAPHY.md](../../../TYPOGRAPHY.md)).
 *
 * tailwind-merge has to be TOLD about them. `text-*` is a namespace it shares
 * between font-size and text-COLOR, and it decides which by testing the value
 * against its own built-in scale (`xs`, `sm`, `[13px]`, …). A custom key like
 * `text-label` is in neither, so it falls through to the color group — and is
 * then dropped as conflicting with the real color beside it:
 *
 *   twMerge('text-label text-content-2')            -> 'text-content-2'
 *   twMerge('text-eyebrow px-2.5 uppercase text-content-3')
 *                                    -> 'px-2.5 uppercase text-content-3'
 *
 * Silently, at runtime, with no build error — so the step's size, weight and
 * tracking all vanished anywhere `cn()` was used with a text color, which is
 * most call sites. Every new step MUST be added here as well as to the
 * Tailwind config.
 */
const kTypeScaleSteps = [
  // EDITORIAL voice (500) — added 2026-09-12 with the Growth RX conversion.
  'hero',
  'figure',
  // UI voice (800), plus the 600-weight body/sub.
  'display',
  'title-lg',
  'title',
  'body',
  'body-strong',
  'cta',
  'sub',
  // 12px: `fine` is PROSE, the other two are CHIP roles.
  'fine',
  'label',
  'eyebrow',
];

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: kTypeScaleSteps }] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// `PopStoreCreatorBusinessType.AIEcho` resolves to `'aiecho'`. Marketing also
// drives the same AIEcho funnel through the `auto_selling_agent` reg-intent
// alias, so both lowercased `utm_regintent` values map to AIEcho.
const AI_ECHO_INTENT = SignupIntent.AIEcho.toLowerCase();
const AUTO_SELLING_AGENT_INTENT = 'auto_selling_agent';

/** Lowercased `utm_regintent` values that map to the AIEcho business type. */
export const aiEchoIntents = new Set<string>([
  AI_ECHO_INTENT,
  AUTO_SELLING_AGENT_INTENT,
]);

export function getOptionLabel(index: number): string {
  let label = '';
  let n = index;
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/**
 * Read the `utm_regintent` value captured by `captureAnalyticsSessionParams`
 * (registered at the root layout via `AnalyticsProvider`). Returns the raw
 * string from sessionStorage — typically a value in `SignupIntent`, but the
 * backend may add new ones, so callers should not assume the return is a
 * known enum member.
 *
 * Falls back to `SignupIntent.Generic` on SSR, a missing bag, a parse error,
 * or a value that is not a string — see {@link capturedParam}.
 */
/**
 * One captured marketing param, as a string — or `undefined` when the bag does
 * not hold a string there.
 *
 * CHECKED, not asserted, and this is the boundary where that has to happen.
 * `JSON.parse` returns `any`; the bag is `sessionStorage`, writable by anything
 * with a console open and carried across deploys that may have written it in
 * another shape. Every caller then does string work on the result — `.trim()`,
 * `.toLowerCase()`, an `===` — so a number or an object in there would throw
 * from inside a render.
 *
 * A NAMED narrowing function rather than a probe at each call site: `unknown`
 * in, a declared type out, in one place. The pure functions downstream take
 * declared `string` params and are entitled to trust them.
 */
function capturedParam(key: string): string | undefined {
  try {
    const stored = sessionStorage.getItem(kAnalyticsSessionParamsKey);
    if (stored == null) return undefined;
    const parsed: unknown = JSON.parse(stored);
    if (parsed == null || typeof parsed !== 'object') return undefined;
    const value: unknown = Reflect.get(parsed, key);
    return typeof value === 'string' ? value : undefined;
  } catch {
    return undefined;
  }
}

export function getSignupIntent(): string {
  if (typeof window === 'undefined') return SignupIntent.Generic;
  return capturedParam('utm_regintent') ?? SignupIntent.Generic;
}

/**
 * Read the `utm_onboarding_path` value captured by
 * `captureAnalyticsSessionParams`, from the same bag as
 * {@link getSignupIntent}.
 *
 * The SECOND param a campaign link can carry, and it is not interchangeable
 * with `utm_regintent`: a link may set either, or both, or the two may
 * disagree — marketing writes them by hand — so a surface that has to know
 * which campaign a creator arrived on has to look at both. `''` for SSR, a
 * missing bag, a parse error or a link that set neither, so a caller can
 * compare it without a null check — and for a value that is not a string, see
 * {@link capturedParam}.
 */
export function getOnboardingPath(): string {
  if (typeof window === 'undefined') return '';
  return capturedParam('utm_onboarding_path') ?? '';
}
